/**
 * Anonymous API client for unauthenticated trial usage.
 * Includes device fingerprint in requests for session tracking.
 */

import axios, { InternalAxiosRequestConfig } from 'axios';
import { getFingerprint } from '../utils/fingerprint';
import { getApiErrorCode, handleApiError } from './client';
import { resolveApiBaseUrl } from './baseUrl';
import type {
  MindmapResponse,
  KeypointsResponse,
  ListSummaryTemplatesResponse,
  SummaryResponse,
  ListTranslationsApiResponse,
  TranslationFile,
  TranslationStatusApiResponse,
  TranslationUpdatesApiResponse,
  UpdateContentResponse,
  RevertResponse,
  SegmentEdit,
} from './transcriptions';

// API base URL from environment variable, defaults to localhost
const API_BASE_URL = resolveApiBaseUrl();

// Anonymous API endpoints
const ANONYMOUS_BASE = '/anonymous';

// Response types
export interface AnonymousQuotaStatus {
  session_id: string;
  files_used_today: number;
  daily_limit: number;
  remaining_files: number;
  max_file_duration_seconds: number;
  reset_at: string;
  can_upload: boolean;
}

export interface RegistrationPrompt {
  title: string;
  message: string;
  benefits: string[];
}

export interface AnonymousInitiateUploadResponse {
  file_id: string;
  upload_url: string;
  expires_at: string;
  exists: boolean;
  instant_upload_available?: boolean;
  instant_upload_token?: string;
}

export interface AnonymousFileResponse {
  id: string;
  name: string;
  original_name: string;
  file_type: string;
  mime_type: string;
  file_size: number;
  duration: number;
  status: string;
  created_at: string;
}

export interface AnonymousTranscriptionResponse {
  transcription_id: string;
  status: string;
  message?: string;
}

export interface AnonymousTranscription {
  id: string;
  file_id: string;
  anonymous_session_id?: string;
  display_name?: string;
  display_name_user_set?: boolean;
  content?: string;
  language?: string;
  target_language?: string;
  duration: number;
  word_count: number;
  speaker_count: number;
  status: string;
  progress: number;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  is_edited?: boolean;
  last_edited_at?: string;
}

export interface AnonymousTranscriptionDetailResponse {
  transcription: AnonymousTranscription;
  registration_prompt?: RegistrationPrompt;
}

export interface TranscriptionResultMetadata {
  language: string;
  duration: number;
  word_count: number;
  speaker_count: number;
}

export interface TranscriptionResultSegment {
  id: number;
  start: number;
  end: number;
  text: string;
  is_modified?: boolean;
  speaker?: string;
  confidence?: number;
}

export interface TranscriptionResultSpeaker {
  id: string;
  name: string;
  segments_count: number;
}

export interface AnonymousTranscriptionResult {
  transcription_id: string;
  version: string;
  created_at: string;
  metadata: TranscriptionResultMetadata;
  content: string;
  segments: TranscriptionResultSegment[];
  speakers?: TranscriptionResultSpeaker[];
}

export interface AnonymousDownloadURLResponse {
  download_url: string;
  expires_at: string;
}

export interface UpdateAnonymousDisplayNameResponse {
  transcription: AnonymousTranscription;
}

export interface TranslateAnonymousTranscriptionResponse {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  target_language: string;
  message?: string;
}

// Create axios instance for anonymous requests
const anonymousApiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor: attach fingerprint header
anonymousApiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const fingerprint = await getFingerprint();
      if (fingerprint && config.headers) {
        config.headers['X-Device-Fingerprint'] = fingerprint;
      }
    } catch (e) {
      console.warn('Failed to get fingerprint:', e);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Get anonymous session quota status.
 */
export async function getAnonymousQuota(): Promise<AnonymousQuotaStatus> {
  const response = await anonymousApiClient.get<AnonymousQuotaStatus>(
    `${ANONYMOUS_BASE}/quota`
  );
  return response.data;
}

/**
 * Get registration prompt content.
 */
export async function getRegistrationPrompt(): Promise<RegistrationPrompt> {
  const response = await anonymousApiClient.get<RegistrationPrompt>(
    `${ANONYMOUS_BASE}/registration-prompt`
  );
  return response.data;
}

/**
 * Initiate an anonymous file upload.
 */
export async function initiateAnonymousUpload(params: {
  file_name: string;
  file_size: number;
  content_type: string;
  estimated_duration_seconds?: number;
  language?: string;
}): Promise<AnonymousInitiateUploadResponse> {
  const response = await anonymousApiClient.post<AnonymousInitiateUploadResponse>(
    `${ANONYMOUS_BASE}/files/upload`,
    params
  );
  return response.data;
}

/**
 * Confirm an anonymous file upload.
 */
export async function confirmAnonymousUpload(
  fileId: string
): Promise<AnonymousFileResponse> {
  const response = await anonymousApiClient.post<AnonymousFileResponse>(
    `${ANONYMOUS_BASE}/files/${fileId}/confirm`
  );
  return response.data;
}

/**
 * Create an anonymous transcription.
 */
export async function createAnonymousTranscription(params: {
  file_id: string;
  language?: string;
  enable_diarization?: boolean;
  max_speakers?: number;
}): Promise<AnonymousTranscriptionResponse> {
  const response = await anonymousApiClient.post<AnonymousTranscriptionResponse>(
    `${ANONYMOUS_BASE}/transcriptions`,
    params
  );
  return response.data;
}

/**
 * Get an anonymous transcription.
 */
export async function getAnonymousTranscription(
  transcriptionId: string
): Promise<AnonymousTranscriptionDetailResponse> {
  const response = await anonymousApiClient.get<AnonymousTranscriptionDetailResponse>(
    `${ANONYMOUS_BASE}/transcriptions/${transcriptionId}`
  );
  return response.data;
}

/**
 * Get an anonymous transcription result.
 */
export async function getAnonymousTranscriptionResult(
  transcriptionId: string
): Promise<AnonymousTranscriptionResult> {
  const response = await anonymousApiClient.get<AnonymousTranscriptionResult>(
    `${ANONYMOUS_BASE}/transcriptions/${transcriptionId}/result?lite=1`
  );
  return response.data;
}

/**
 * Get a presigned download URL for an anonymous file.
 */
export async function getAnonymousDownloadURL(fileId: string): Promise<AnonymousDownloadURLResponse> {
  const response = await anonymousApiClient.get<AnonymousDownloadURLResponse>(
    `${ANONYMOUS_BASE}/files/${fileId}/download`
  );
  return response.data;
}

/**
 * Update display name for an anonymous transcription.
 */
export async function updateAnonymousTranscriptionDisplayName(
  transcriptionId: string,
  displayName: string
): Promise<UpdateAnonymousDisplayNameResponse> {
  const response = await anonymousApiClient.put<UpdateAnonymousDisplayNameResponse>(
    `${ANONYMOUS_BASE}/transcriptions/${transcriptionId}/display-name`,
    { display_name: displayName }
  );
  return response.data;
}

/**
 * Update transcript segments for an anonymous transcription.
 */
export async function updateAnonymousTranscriptionContent(
  transcriptionId: string,
  segments: SegmentEdit[]
): Promise<UpdateContentResponse> {
  const response = await anonymousApiClient.put<UpdateContentResponse>(
    `${ANONYMOUS_BASE}/transcriptions/${transcriptionId}/content`,
    { segments }
  );
  return response.data;
}

/**
 * Revert an anonymous transcription back to the original content.
 */
export async function revertAnonymousTranscription(transcriptionId: string): Promise<RevertResponse> {
  const response = await anonymousApiClient.post<RevertResponse>(
    `${ANONYMOUS_BASE}/transcriptions/${transcriptionId}/revert`
  );
  return response.data;
}

/**
 * Start translation for an anonymous transcription.
 */
export async function translateAnonymousTranscription(
  transcriptionId: string,
  targetLanguage: string
): Promise<TranslateAnonymousTranscriptionResponse> {
  const response = await anonymousApiClient.post<TranslateAnonymousTranscriptionResponse>(
    `${ANONYMOUS_BASE}/transcriptions/${transcriptionId}/translate`,
    { target_language: targetLanguage }
  );
  return response.data;
}

export async function listAnonymousTranslations(transcriptionId: string): Promise<ListTranslationsApiResponse> {
  const response = await anonymousApiClient.get<ListTranslationsApiResponse>(
    `${ANONYMOUS_BASE}/transcriptions/${transcriptionId}/translations`
  );
  return response.data;
}

export async function getAnonymousTranslation(transcriptionId: string, lang: string): Promise<TranslationFile> {
  const response = await anonymousApiClient.get<TranslationFile>(
    `${ANONYMOUS_BASE}/transcriptions/${transcriptionId}/translations/${lang}`
  );
  return response.data;
}

export async function getAnonymousTranslationStatus(
  transcriptionId: string,
  lang: string
): Promise<TranslationStatusApiResponse> {
  const response = await anonymousApiClient.get<TranslationStatusApiResponse>(
    `${ANONYMOUS_BASE}/transcriptions/${transcriptionId}/translations/${lang}/status`
  );
  return response.data;
}

export async function getAnonymousTranslationUpdates(
  transcriptionId: string,
  lang: string,
  cursor: number = 0,
  limit: number = 200
): Promise<TranslationUpdatesApiResponse> {
  const response = await anonymousApiClient.get<TranslationUpdatesApiResponse>(
    `${ANONYMOUS_BASE}/transcriptions/${transcriptionId}/translations/${lang}/updates`,
    { params: { cursor, limit } }
  );
  return response.data;
}

export async function deleteAnonymousTranslation(transcriptionId: string, lang: string): Promise<void> {
  await anonymousApiClient.delete(`${ANONYMOUS_BASE}/transcriptions/${transcriptionId}/translations/${lang}`);
}

export async function requestAnonymousMindmap(transcriptionId: string): Promise<MindmapResponse> {
  const response = await anonymousApiClient.post<MindmapResponse>(
    `${ANONYMOUS_BASE}/transcriptions/${transcriptionId}/mindmap`
  );
  return response.data;
}

export async function getAnonymousMindmap(transcriptionId: string): Promise<MindmapResponse> {
  const response = await anonymousApiClient.get<MindmapResponse>(
    `${ANONYMOUS_BASE}/transcriptions/${transcriptionId}/mindmap`
  );
  return response.data;
}

export async function listAnonymousSummaryTemplates(): Promise<ListSummaryTemplatesResponse> {
  const response = await anonymousApiClient.get<ListSummaryTemplatesResponse>(`${ANONYMOUS_BASE}/summary/templates`);
  return response.data;
}

export async function requestAnonymousSummary(transcriptionId: string, templateId: string): Promise<SummaryResponse> {
  const response = await anonymousApiClient.post<SummaryResponse>(`${ANONYMOUS_BASE}/transcriptions/${transcriptionId}/summary`, {
    template_id: templateId,
  });
  return response.data;
}

export async function getAnonymousSummary(transcriptionId: string, templateId: string): Promise<SummaryResponse> {
  const response = await anonymousApiClient.get<SummaryResponse>(`${ANONYMOUS_BASE}/transcriptions/${transcriptionId}/summary`, {
    params: { template_id: templateId },
  });
  return response.data;
}

export async function requestAnonymousKeypoints(transcriptionId: string, outputLanguage: string): Promise<KeypointsResponse> {
  const response = await anonymousApiClient.post<KeypointsResponse>(`${ANONYMOUS_BASE}/transcriptions/${transcriptionId}/keypoints`, {
    output_language: outputLanguage,
  });
  return response.data;
}

export async function getAnonymousKeypoints(transcriptionId: string, outputLanguage: string): Promise<KeypointsResponse> {
  const response = await anonymousApiClient.get<KeypointsResponse>(`${ANONYMOUS_BASE}/transcriptions/${transcriptionId}/keypoints`, {
    params: { output_language: outputLanguage },
  });
  return response.data;
}

/**
 * Check if user can use anonymous trial (quota check).
 */
export async function canUseAnonymousTrial(): Promise<{
  canUpload: boolean;
  quota: AnonymousQuotaStatus;
}> {
  try {
    const quota = await getAnonymousQuota();
    return {
      canUpload: quota.can_upload,
      quota,
    };
  } catch (error) {
    // If we get a 429, quota is exceeded
    if (axios.isAxiosError(error) && error.response?.status === 429) {
      return {
        canUpload: false,
        quota: {
          session_id: '',
          files_used_today: 1,
          daily_limit: 1,
          remaining_files: 0,
          max_file_duration_seconds: 3600,
          reset_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          can_upload: false,
        },
      };
    }
    throw error;
  }
}

// Re-export error handler
export { getApiErrorCode, handleApiError };

export default anonymousApiClient;
